import {
  Card,
  Grid,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import {
  cleanAddJobPhaseCostsInput,
  JobPhaseCostItemInputSchema,
} from "DDD/action-objects/JobPhaseCostItemCreate";
import { UpdateJobPhaseNotesInputSchema } from "DDD/action-objects/JobPhaseNotesUpdate";
import { AddJobPhaseCostsInput, UpdateJobPhaseNotesInput } from "generated/graphql";
import { useForm } from "react-hook-form";

import useAddJobPhaseCosts from "hooks/jobs/job-phases/useAddJobPhaseCosts";
import React, { useCallback, useState } from "react";

import { JobPhaseContext } from "./context/JobPhaseContext";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";

import useGetJobPhaseCostItem from "hooks/jobs/job-phases/useGetJobPhaseCostItem";
import JobPhaseCost from "./forms/JobPhaseCost";
import JobPhaseNotesAndFiles from "./forms/JobPhaseNotesAndFiles";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import MDAlert from "components/MDAlert";
import useUpdateJobPhaseNotes from "hooks/jobs/job-phases/useUpdateJobPhaseNotes";
import JobCostingSection from "./JobCostingSection";
import useUpdateJobPhaseCosting from "hooks/jobs/job-phases/useUpdateJobPhaseCosting";
import JobCostingHeader from "./JobCostingHeader";
import SubcontractorCostingFields from "./SubcontractorCostingFields";
import ViewStatusSelect from "./ViewStatusSelect";
import {
  CostingMode,
  getSubcontractorCostingDefaults,
  STANDARD_COSTING_MODE,
  SUBCONTRACTED_COSTING_MODE,
  SubcontractorCostingFormValues,
} from "./jobPhaseCosting";

const View = React.memo(function ({
  jobPhase,
  addPhaseCostCompleted,
}: {
  jobPhase: CustomJobPhase;
  addPhaseCostCompleted: () => void;
}) {
  const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
  const today = new Date(Date.now() - tzoffset).toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const {
    loading: loadingJobPhaseCostItem,
    data: jobPhaseCostItem,
    error: errorJobPhaseCostItem,
  } = useGetJobPhaseCostItem({ date, jobPhaseId: jobPhase.id });

  const [updateJobPhaseCosting, { loading: loadingCosting }] = useUpdateJobPhaseCosting();
  const [updateJobPhaseNotes, { loading: loadingNotes }] = useUpdateJobPhaseNotes();
  const [addJobPhaseCosts, { loading: loadingCosts }] = useAddJobPhaseCosts(addPhaseCostCompleted);
  const {
    control: subcontractorCostingControl,
    getValues: getSubcontractorCostingValues,
    handleSubmit: handleSubcontractorCostingSubmit,
    reset: resetSubcontractorCostingForm,
    watch: watchSubcontractorCosting,
  } = useForm<SubcontractorCostingFormValues>({
    defaultValues: getSubcontractorCostingDefaults(jobPhase),
  });
  const costingMode = watchSubcontractorCosting("costingMode");

  const onSubmitJobPhaseCosts = useCallback(async (values: JobPhaseCostItemInputSchema) => {
    const cleanedValues = cleanAddJobPhaseCostsInput(values);
    await addJobPhaseCosts({
      variables: { input: cleanedValues as AddJobPhaseCostsInput },
    });
  }, []);

  const onSubmitJobPhaseNotes = useCallback(async (values: UpdateJobPhaseNotesInputSchema) => {
    await updateJobPhaseNotes({
      variables: { input: values as UpdateJobPhaseNotesInput },
    });
  }, []);

  const changeDate = (selectedDate: Date[]) => {
    const d = selectedDate[0]; // local date chosen by user
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setDate(`${y}-${m}-${day}`); // "YYYY-MM-DD"
  };

  const getUpdateJobPhaseCostingInput = (
    values: SubcontractorCostingFormValues,
    costingModeOverride?: CostingMode
  ) => ({
    id: jobPhase.id,
    costingMode: costingModeOverride ?? values.costingMode,
    actualSubcontractorCost: values.actualSubcontractorCost
      ? Math.round(Number(values.actualSubcontractorCost) * 100)
      : null,
    actualSubcontractorName: values.actualSubcontractorName || null,
    actualSubcontractorReference: values.actualSubcontractorReference || null,
  });

  const handleCostingModeChange = async (newMode: CostingMode) => {
    const values = getSubcontractorCostingValues();
    const result = await updateJobPhaseCosting({
      variables: {
        input: getUpdateJobPhaseCostingInput(values, newMode),
      },
    });

    if (!result?.success) {
      resetSubcontractorCostingForm(getSubcontractorCostingDefaults(jobPhase));
    }
  };

  const handleSaveSubcontractorDetails = useCallback(async (values: SubcontractorCostingFormValues) => {
    await updateJobPhaseCosting({
      variables: {
        input: getUpdateJobPhaseCostingInput(values),
      },
    });
  }, [jobPhase.id, updateJobPhaseCosting]);

  return (
    <JobPhaseContext.Provider value={{ jobPhase, jobPhaseCostItem, date }}>
      <Grid item xs={12} display="flex" justifyContent="flex-end" paddingRight={3}>
        <MDBox display="flex" gap={2} flexWrap="wrap" justifyContent="flex-end">
          <ViewStatusSelect jobPhaseId={jobPhase.id} status={jobPhase?.status} />
        </MDBox>
      </Grid>
      <Card>
        <MDBox p={4}>
          <MDBox my={4}>
            <JobCostingSection jobPhase={jobPhase} />
          </MDBox>
          <JobCostingHeader
            control={subcontractorCostingControl}
            loading={loadingCosting}
            today={today}
            onDateChange={changeDate}
            onCostingModeChange={async (newMode, onFieldChange) => {
              onFieldChange(newMode);
              await handleCostingModeChange(newMode);
            }}
          />
          {costingMode === SUBCONTRACTED_COSTING_MODE && (
            <SubcontractorCostingFields
              control={subcontractorCostingControl}
              loading={loadingCosting}
              handleSubmit={handleSubcontractorCostingSubmit}
              onSubmit={handleSaveSubcontractorDetails}
            />
          )}
          {loadingJobPhaseCostItem && <CircularProgress color="inherit" size={20} />}
          {errorJobPhaseCostItem && (
            <MDAlert color="error">{parseGraphQLError(errorJobPhaseCostItem)}</MDAlert>
          )}
          {costingMode === STANDARD_COSTING_MODE && !loadingJobPhaseCostItem && !errorJobPhaseCostItem && (
            <JobPhaseCost key={date} saving={loadingCosts} onSubmit={onSubmitJobPhaseCosts} />
          )}
        </MDBox>
      </Card>
      <JobPhaseNotesAndFiles onSubmit={onSubmitJobPhaseNotes} saving={loadingNotes} />
    </JobPhaseContext.Provider>
  );
});

View.displayName = "JobPhaseView";

export default View;
